import { BellRing, Edit2Icon, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import EditModal from "../../components/modal/EditModal";
import DeleteModal from "../../components/modal/DeleteModal";
import { getToken } from "../../app/auth/authConfig";
import { getTagOptions } from "./tagStorage";

const ToolManagement = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState("Member");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const toolsURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/categories/`;

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const token = await getToken();
      const res = await axios.get(toolsURL, {
        headers: { "oidc-token": token },
      });
      setCategories(res.data || []);
    } catch (e) {
      console.error("Tool fetch error", e);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    fetchTools();
    fetchCategories();
    fetchTagOptions();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = await getToken();
      if (token) {
        // Decode JWT to get user info
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const decodedToken = JSON.parse(jsonPayload);
        const email = decodedToken.preferred_username || decodedToken.email || null;
        setCurrentUserEmail(email);
        
        // Fetch user role from backend
        if (email) {
          try {
            const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/users/`;
            const res = await axios.get(baseURL, {
              headers: { "oidc-token": token },
            });
            const users = res.data;
            const currentUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
            setCurrentUserRole(currentUser?.role || "Member");
          } catch (err) {
            console.error("Error fetching user role", err);
            setCurrentUserRole("Member");
          }
        }
      }
    } catch (err) {
      console.error("Error decoding token", err);
    }
  };

  const fetchTagOptions = async () => {
    try {
      const options = await getTagOptions();
      setTagOptions(options || []);
    } catch (e) {
      console.error("Tag options fetch error", e);
      setTagOptions([]);
    }
  };

  const fetchTools = async () => {
    try {
      setIsLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/`;
      const token = await getToken();

      const res = await axios.get(baseURL,{
        headers: { "oidc-token": token },
      });
      setTools(res.data);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTools = tools.filter(
    (t) =>
      t.toolName.toLowerCase().includes(search.toLowerCase()) ||
      t.tooltype.toLowerCase().includes(search.toLowerCase()) ||
      t.url.toLowerCase().includes(search.toLowerCase()) ||
      (t.tags || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTools.length / perPage);
  const paginatedTools = filteredTools.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const handleDelete = async () => {
    if (deleteIndex === null || deleteIndex === undefined) return;

    try {
      setIsDeleting(true);
      const tool = filteredTools[deleteIndex];
      if (!tool?.id) {
        setToast({ type: "error", message: "❌ Unable to find selected tool" });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/${tool.id}`;
      const token = await getToken();

      await axios.delete(baseURL, {
        headers: { "oidc-token": token },
      });

      setToast({ type: "success", message: "✅ Tool marked as inactive successfully!" });
      setTimeout(() => setToast(null), 3000);
      setShowDeleteModal(false);
      setDeleteIndex(null);
      await fetchTools();
    } catch (err) {
      console.error("Delete error", err);
      const errorMsg = err?.response?.data?.error || "Error deleting tool";
      setToast({ type: "error", message: `❌ ${errorMsg}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  const catagoriesOptions = useMemo(() => {
    const categoryOptionsFromMaster = (categories || [])
      .map((c) => (c?.categoryName || "").trim())
      .filter(Boolean)
      .map((name) => ({ label: name, value: name }));

    // Keep current tool types as fallback so existing values are still selectable
    // even if category master data is temporarily missing or out-of-sync.
    const categoryOptionsFromTools = (tools || [])
      .map((t) => (t?.tooltype || "").trim())
      .filter(Boolean)
      .map((name) => ({ label: name, value: name }));

    const merged = [...categoryOptionsFromMaster, ...categoryOptionsFromTools];
    return [...new Map(merged.map((opt) => [opt.value, opt])).values()];
  }, [categories, tools]);

  const handleToolSave = async (formData) => {
    try {
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/${editData.id}`;
      const token = await getToken();

      const payload = new FormData();
      payload.append("toolName", formData.toolName || "");
      payload.append("ownerEmail", formData.ownerEmail || "");
      payload.append("tooltype", formData.tooltype || "");
      payload.append("url", formData.url || "");
      payload.append("description", formData.description || "");
      payload.append(
        "tags",
        Array.isArray(formData.tags) ? formData.tags.join(",") : (formData.tags || "")
      );
      payload.append(
        "existingProductDocuments",
        Array.isArray(formData.productDocumentsCurrent)
          ? formData.productDocumentsCurrent.join(",")
          : ""
      );
      payload.append(
        "existingOnboardingFiles",
        Array.isArray(formData.onboardingFilesCurrent)
          ? formData.onboardingFilesCurrent.join(",")
          : ""
      );

      (formData.productDocuments || []).forEach((file) => {
        payload.append("productDocuments", file);
      });

      (formData.onboardingFiles || []).forEach((file) => {
        payload.append("onboardingFiles", file);
      });

      await axios.put(baseURL, payload, {
        headers: { "oidc-token": token },
      });

      setShowEditModal(false);
      await fetchTools();
    } catch (err) {
      console.error("Update error", err);
    }
  };

  const handleReminder = async (tool) => {
    try {
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/${tool.id}/remind`;
      const token = await getToken();

      const res = await axios.post(
        baseURL,
        {},
        {
          headers: { "oidc-token": token },
        }
      );

      alert(res.data?.message || "Reminder sent successfully!");
    } catch (err) {
      console.error("Reminder error", err);
      alert(err.response?.data?.error || "Error sending reminder");
    }
  };

  const canEditTool = (tool) => {
    if (currentUserRole === "Admin") return true;
    if (currentUserRole === "Member" && currentUserEmail && tool.ownerEmail) {
      return currentUserEmail.toLowerCase() === tool.ownerEmail.toLowerCase();
    }
    return false;
  };

  const canDeleteTool = (tool) => {
    if (currentUserRole === "Admin") return true;
    if (currentUserRole === "Member" && currentUserEmail && tool.ownerEmail) {
      return currentUserEmail.toLowerCase() === tool.ownerEmail.toLowerCase();
    }
    return false;
  };
  
  

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          padding: 32,
        }}
      >
        <input
          type="text"
          placeholder="Search Tool Name, Type, URL"
          value={search}
          disabled={isLoading}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{
            padding: 8,
            width: 300,
            marginBottom: 16,
            borderRadius: 4,
            border: "1px solid #ccc",
            backgroundColor: isLoading ? "#f0f0f0" : "white",
            cursor: isLoading ? "not-allowed" : "auto",
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <strong className="pb-2">Total Tools: {filteredTools.length}</strong>
          <div>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={isLoading || page === 1}
              style={{
                cursor: (isLoading || page === 1) ? "not-allowed" : "pointer",
                opacity: (isLoading || page === 1) ? 0.6 : 1,
              }}
            >
              Prev
            </button>
            <span style={{ margin: "0 8px" }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={isLoading || page === totalPages}
              style={{
                cursor: (isLoading || page === totalPages) ? "not-allowed" : "pointer",
                opacity: (isLoading || page === totalPages) ? 0.6 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
            fontSize: "14px",
          }}>
            <thead>
              <tr style={{ height: "50px" }}>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  borderRadius: "8px 0 0 8px",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  🔧 Tool Name
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  📂 Type
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  🔗 URL
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  📝 Description
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  🏷️ Tags
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  📧 Owner Email
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "center",
                  borderRadius: "0 8px 8px 0",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  ⚙️ Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: perPage }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} style={{
                    background: idx % 2 === 0 ? "#ffffff" : "#f3f0f7",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    border: "1px solid #e5e5e5",
                  }}>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "8px 0 0 8px",
                    }}>
                      <div className="h-4 bg-gray-300 rounded w-32 animate-pulse" />
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-20 animate-pulse" />
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-40 animate-pulse" />
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-32 animate-pulse" />
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-24 animate-pulse" />
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div className="h-4 bg-gray-300 rounded w-36 animate-pulse" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "0 8px 8px 0",
                    }}>
                      <div className="flex gap-2 justify-center">
                        <div className="h-8 bg-gray-300 rounded w-16 animate-pulse" />
                        <div className="h-8 bg-gray-300 rounded w-16 animate-pulse" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : paginatedTools.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "#f8f8f8",
                    borderRadius: "8px",
                  }}>
                    <span style={{ color: "#999", fontSize: "15px" }}>
                      📭 No tools found
                    </span>
                  </td>
                </tr>
              ) : (
                paginatedTools.map((tool, idx) => (
                <tr key={tool.id} style={{
                  background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  border: "1px solid #e5e5e5",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}>
                  <td style={{
                    padding: "14px 16px",
                    borderRadius: "8px 0 0 8px",
                    fontWeight: "500",
                    color: "#333",
                  }}>
                    {tool.toolName}
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    color: "#666",
                  }}>
                    <span style={{
                      background: "#e3f2fd",
                      color: "#1565c0",
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      display: "inline-block",
                    }}>
                      {tool.tooltype}
                    </span>
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    color: "#666",
                    maxWidth: "250px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    <a href={tool.url} target="_blank" rel="noopener noreferrer" style={{
                      color: "#bb1ccc",
                      textDecoration: "none",
                      fontSize: "12px",
                    }}>
                      {tool.url}
                    </a>
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    color: "#666",
                    maxWidth: "200px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {tool.description}
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    color: "#666",
                    maxWidth: "160px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }} title={tool.tags || ""}>
                    {tool.tags ? (
                      <span style={{
                        background: "#f3e8ff",
                        color: "#7e22ce",
                        padding: "4px 10px",
                        borderRadius: "14px",
                        fontSize: "12px",
                        fontWeight: "600",
                        display: "inline-block",
                      }}>
                        {tool.tags}
                      </span>
                    ) : (
                      <span style={{ color: "#999", fontSize: "12px" }}>No tags</span>
                    )}
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    color: "#555",
                    maxWidth: "200px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "13px",
                  }} title={tool.ownerEmail || ""}>
                    {tool.ownerEmail ? (
                      <a href={`mailto:${tool.ownerEmail}`} style={{ color: "#bb1ccc", textDecoration: "none" }}>
                        {tool.ownerEmail}
                      </a>
                    ) : (
                      <span style={{ color: "#999", fontSize: "12px" }}>N/A</span>
                    )}
                  </td>
                  <td style={{
                    padding: "14px 16px",
                    borderRadius: "0 8px 8px 0",
                    textAlign: "center",
                  }}>
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => navigate(`/details/${tool.id}`)}
                        style={{
                          background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow = "0 4px 8px rgba(59,130,246,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        👁️ View
                      </button>
                      {tool.toolsStatus !== "approved" && (
                        <button
                          onClick={() => handleReminder(tool)}
                          style={{
                            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(245,158,11,0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          title="Send reminder"
                        >
                          <BellRing width={14} /> Reminder
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowEditModal(true);
                          setEditData({
                            ...tool,
                            tags: tool.tags
                              ? tool.tags
                                  .split(",")
                                  .map((tag) => tag.trim())
                                  .filter(Boolean)
                              : [],
                          });
                        }}
                        disabled={!canEditTool(tool)}
                        title={!canEditTool(tool) ? "You can only edit tools you own" : "Edit tool"}
                        style={{
                          background: canEditTool(tool) ? "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" : "#ccc",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: canEditTool(tool) ? "pointer" : "not-allowed",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.3s ease",
                          opacity: canEditTool(tool) ? 1 : 0.6,
                        }}
                        onMouseEnter={(e) => {
                          if (canEditTool(tool)) {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(187,28,204,0.3)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <Edit2Icon width={14} /> Edit
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteModal(true);
                          setDeleteIndex((page - 1) * perPage + idx);
                        }}
                        disabled={!canDeleteTool(tool)}
                        title={!canDeleteTool(tool) ? "You can only delete tools you own" : "Delete tool"}
                        style={{
                          background: canDeleteTool(tool) ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" : "#ccc",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: canDeleteTool(tool) ? "pointer" : "not-allowed",
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.3s ease",
                          opacity: canDeleteTool(tool) ? 1 : 0.6,
                        }}
                        onMouseEnter={(e) => {
                          if (canDeleteTool(tool)) {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(239,68,68,0.3)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <Trash2 width={14} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>


      {showEditModal && editData && (
        <EditModal
          title="Edit Tool"
          fields={[
            { label: "Owner Email", key: "ownerEmail", type: "email" },
            { label: "Tool Name", key: "toolName" },
            { label: "Type", key: "tooltype" ,type: "select", options: catagoriesOptions},
            { label: "URL", key: "url" },
            { label: "Description", key: "description", type: "textarea", rows: 4, fullWidth: true },
            { label: "Tags", key: "tags", type: "multiselect", options: tagOptions },
            {
              label: "Product Documents",
              key: "productDocuments",
              type: "file",
              multiple: true,
              accept: ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx",
              currentValue: editData.filePath,
              fullWidth: true,
            },
            {
              label: "Onboarding Documents",
              key: "onboardingFiles",
              type: "file",
              multiple: true,
              accept: ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx",
              currentValue: editData.onboardingFilePath,
              fullWidth: true,
            },
          ]}
          data={editData}
          onClose={() => setShowEditModal(false)}
          onSave={handleToolSave}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          title="Are you sure you want to delete this tool?"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 250,
            maxWidth: 420,
            padding: "12px 18px",
            borderRadius: 8,
            color: "#fff",
            fontWeight: 500,
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            animation: "slideInToast 0.3s ease",
          }}
        >
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideInToast {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default ToolManagement;
